import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

const QuestionnaireCompletionBanner = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) {
        setLoading(false);
        return;
      }

      try {
        // Fetch user data
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const user = userDoc.data();
          setUserData(user);

          // Fetch project data if user has currentProjectId
          const projectId = user.currentProjectId || user.activeProjectId;
          if (projectId) {
            const projectDoc = await getDoc(doc(db, 'projects', projectId));
            if (projectDoc.exists()) {
              setProjectData(projectDoc.data());
            }
          }
        }
      } catch (error) {
        console.error('Error fetching data for questionnaire banner:', error);
      }

      setLoading(false);
    };

    fetchData();
  }, [currentUser]);

  // Only show banner if:
  // 1. User is logged in
  // 2. User is tier 3 (or tier 2 for dynamic reports)
  // 3. User has NOT completed the questionnaire
  if (loading || !currentUser || !userData) {
    return null;
  }

  // Check if questionnaire is completed - supports both old and new (ADR-028) data structures
  const hasCompletedQuestionnaire =
    // New versioned system (ADR-028) - check for readable questionnaire ID
    projectData?.currentReadableQuestionnaireId ||
    // Old system - check for questionnaire_v1 data with active status
    (projectData?.questionnaire_v1 &&
     Object.keys(projectData.questionnaire_v1).length > 0 &&
     projectData?.questionnaire_status === 'active') ||
    // Fallback - if there's an active report, questionnaire must be complete
    projectData?.activeReportId ||
    projectData?.currentReadableReportId;

  const isTier2OrTier3 = userData?.tier === 'tier2' || userData?.tier === 'tier3';

  if (!isTier2OrTier3 || hasCompletedQuestionnaire) {
    return null;
  }

  return (
    <>
      <style>{`
        .questionnaire-banner {
          background: #fef7e0;
          border-bottom: 1px solid #f9ab00;
          padding: 10px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            transform: translateY(-100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .questionnaire-banner-text {
          font-size: 14px;
          color: #5f6368;
          font-family: 'Google Sans', sans-serif;
        }

        .questionnaire-banner-text strong {
          color: #202124;
          font-weight: 600;
        }

        .questionnaire-button {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          background: #f9ab00;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          font-family: 'Google Sans', sans-serif;
        }

        .questionnaire-button:hover {
          background: #ea8600;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .questionnaire-arrow {
          font-size: 16px;
        }

        @media (max-width: 768px) {
          .questionnaire-banner {
            flex-direction: column;
            align-items: flex-start;
            padding: 10px 16px;
            gap: 10px;
          }

          .questionnaire-banner-text {
            width: 100%;
          }

          .questionnaire-button {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>

      <div className="questionnaire-banner">
        <p className="questionnaire-banner-text">
          <strong>Complete your upgrade questionnaire</strong> to unlock your Dynamic report and SPAR Score analysis.
        </p>
        <button
          onClick={() => navigate('/hub?openQuestionnaire=true')}
          className="questionnaire-button"
        >
          <span>Complete Now</span>
          <span className="questionnaire-arrow">→</span>
        </button>
      </div>
    </>
  );
};

export default QuestionnaireCompletionBanner;
